import { useState } from "react"

import { OnboardingDebtScheduleStateEnum } from "@boundless/bl-api-client/customer/models/OnboardingDebtScheduleStateEnum"
import {
  getCustomerDebtScheduleListQueryKey,
  getCustomerRepositoryDebtScheduleRetrieveQueryKey,
  useCustomerCompanyPartialUpdate,
  useCustomerCompanyRetrieveSuspense,
  useCustomerDebtScheduleListSuspense,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { DebtNotes } from "@/components/debt-table/DebtNotes"
import { DebtStateChoice } from "@/components/debt-table/DebtStateChoice"
import { DebtTable } from "@/components/debt-table/DebtTable"
import { CompanyFlowContext } from "@/lib/flows/company"

export const Route = createFileRoute("/_dashboard/company/debt-table")({
  component: DebtTableView,
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
})

export function DebtTableView() {
  const { companyUuid } = Route.useRouteContext()
  const queryClient = useQueryClient()

  const flowActorRef = CompanyFlowContext.useActorRef()
  const goNext = () => flowActorRef.send({ type: "next" })
  const goBack = () => flowActorRef.send({ type: "back" })

  const { data: debtList } = useCustomerDebtScheduleListSuspense(companyUuid)
  const { data: company, queryKey: companyQueryKey } =
    useCustomerCompanyRetrieveSuspense(companyUuid)

  // Only `submitted` and `no-debt` preselect. A borrower who skipped picks
  // again rather than seeing "Not sure yet" already chosen for them.
  const [choice, setChoice] = useState<OnboardingDebtScheduleStateEnum | null>(
    () => {
      if (
        debtList.length ||
        company.onboardingDebtScheduleState ===
          OnboardingDebtScheduleStateEnum.submitted
      ) {
        return OnboardingDebtScheduleStateEnum.submitted
      }
      if (
        company.onboardingDebtScheduleState ===
        OnboardingDebtScheduleStateEnum["no-debt"]
      ) {
        return OnboardingDebtScheduleStateEnum["no-debt"]
      }
      return null
    },
  )

  const { mutate: updateCompany, isPending } = useCustomerCompanyPartialUpdate({
    mutation: {
      onSuccess: async (data) => {
        queryClient.setQueryData(companyQueryKey, data)
        // A changed answer can retire the rows and replaces the document.
        // Wait for both, or going Back preselects from the stale rows.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getCustomerDebtScheduleListQueryKey(companyUuid),
          }),
          queryClient.invalidateQueries({
            queryKey:
              getCustomerRepositoryDebtScheduleRetrieveQueryKey(companyUuid),
          }),
        ])
        goNext()
      },
    },
  })

  // Coming from the summary means reviewing an answer, not deferring it.
  // The flow drops `returnTo` from the URL and keeps it as the Next override.
  const isFromSummary = CompanyFlowContext.useSelector(
    (state) => state.context.onNextOverride !== undefined,
  )
  const skipBlockedReason = isFromSummary
    ? "Not available when updating your answer"
    : debtList.length > 0
      ? "Remove your debts to skip"
      : null

  const isFillingOut = choice === OnboardingDebtScheduleStateEnum.submitted
  const canContinue = choice !== null && (!isFillingOut || debtList.length > 0)

  // Always send the choice, even when it matches what is stored: the
  // borrower confirming a selection is the action worth recording.
  const handleNext = () => {
    if (!choice) {
      return
    }
    updateCompany({
      uuid: companyUuid,
      data: { onboardingDebtScheduleState: choice },
    })
  }

  return (
    <>
      <div className="flex flex-col rounded-lg bg-white p-4 text-sm shadow-sm sm:p-6">
        <DebtStateChoice
          value={choice}
          onChange={setChoice}
          skipBlockedReason={skipBlockedReason}
        />

        {isFillingOut && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <DebtTable companyUuid={companyUuid} />
          </div>
        )}

        <div className="mt-auto flex flex-col self-stretch pt-6 sm:self-end sm:pt-8">
          <div className="flex flex-col gap-2 sm:flex-row">
            <ButtonBack onClick={goBack} className="w-full sm:w-auto" />
            <ButtonNext
              type="button"
              onClick={handleNext}
              disabled={!canContinue || isPending}
              isLoading={isPending}
              className="w-full sm:w-auto"
            />
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-col rounded-lg bg-white p-4 text-sm shadow-sm sm:p-6">
        <DebtNotes companyUuid={companyUuid} />
      </div>
    </>
  )
}
